import { CHART_SIZE_MENU_OPTION_CUSTOM } from '@/enums/chart-size-menu-options';
import { COLOR_SCHEME_CUSTOM } from '@/enums/color-schemes';
import { CHART_TYPE_OPTION_PERFORMANCE_PERCENT, CHART_TYPE_OPTION_KAGI, CHART_TYPE_OPTION_RENKO, CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';
import { getRangeObjFromRangeTitle } from '@/enums/range-options';
import { ChartStyle, isChartStyle } from '@/interfaces/chart';

function areRangeBarAndGapPropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    chartStyleJson1.rangeBar === chartStyleJson2.rangeBar &&
    chartStyleJson1.rangeGap === chartStyleJson2.rangeGap
  );
}

function areRangeDaysMonthsAndYearsPropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    chartStyleJson1.rangeDays === chartStyleJson2.rangeDays &&
    chartStyleJson1.rangeMonths === chartStyleJson2.rangeMonths &&
    chartStyleJson1.rangeYears === chartStyleJson2.rangeYears
  );
}

function areRangeStartAndEndDatePropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    chartStyleJson1.rangeStartDate === chartStyleJson2.rangeStartDate &&
    chartStyleJson1.rangeEndDate === chartStyleJson2.rangeEndDate
  );
}

function areCustomColorSchemePropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    chartStyleJson1.customColorSchemeBackgroundColor === chartStyleJson2.customColorSchemeBackgroundColor &&
    chartStyleJson1.customColorSchemeGridColor === chartStyleJson2.customColorSchemeGridColor &&
    chartStyleJson1.customColorSchemeTextColor === chartStyleJson2.customColorSchemeTextColor &&
    chartStyleJson1.customColorSchemeIndicatorColor === chartStyleJson2.customColorSchemeIndicatorColor
  );
}

function areCustomWidthAndHeightPropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    chartStyleJson1.customWidth === chartStyleJson2.customWidth &&
    chartStyleJson1.customHeight === chartStyleJson2.customHeight
  );
}

function arePerfChartPropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    arePerfChartSymbolsIdentical(chartStyleJson1, chartStyleJson2) &&
    chartStyleJson1.useMainChartSymbolAsBaseline === chartStyleJson2.useMainChartSymbolAsBaseline
  );
}

function arePerformancePercentPropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    chartStyleJson1.useMainChartSymbolAsBaseline === chartStyleJson2.useMainChartSymbolAsBaseline
  );
}

function areKagiPropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    chartStyleJson1.kagiReversal === chartStyleJson2.kagiReversal &&
    chartStyleJson1.kagiUnit === chartStyleJson2.kagiUnit &&
    chartStyleJson1.kagiPriceField === chartStyleJson2.kagiPriceField
  );
}

function areRenkoPropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    chartStyleJson1.renkoBoxSize === chartStyleJson2.renkoBoxSize &&
    chartStyleJson1.renkoUnit === chartStyleJson2.renkoUnit &&
    chartStyleJson1.renkoPriceField === chartStyleJson2.renkoPriceField
  );
}

function areChartTypesPropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  if (chartStyleJson1.chartType !== chartStyleJson2.chartType)
    return false;

  if (
    chartStyleJson1.chartType === CHART_TYPE_OPTION_PERF_CHART.value &&
    !arePerfChartPropertiesIdentical(chartStyleJson1, chartStyleJson2)
  )
    return false;

  if (
    chartStyleJson1.chartType === CHART_TYPE_OPTION_PERFORMANCE_PERCENT.value &&
    !arePerformancePercentPropertiesIdentical(chartStyleJson1, chartStyleJson2)
  )
    return false;

  if (
    chartStyleJson1.chartType === CHART_TYPE_OPTION_KAGI.value &&
    !areKagiPropertiesIdentical(chartStyleJson1, chartStyleJson2)
  )
    return false;

  return (
    chartStyleJson1.chartType !== CHART_TYPE_OPTION_RENKO.value ||
    areRenkoPropertiesIdentical(chartStyleJson1, chartStyleJson2)
  );
}

function areRangePropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  if (
    chartStyleJson1.range !== chartStyleJson2.range ||
    chartStyleJson1.rangeExtraBars !== chartStyleJson2.rangeExtraBars
  )
    return false;

  const rangeOption = getRangeObjFromRangeTitle(chartStyleJson1.range);

  if (
    rangeOption.useBarAndGap &&
    !areRangeBarAndGapPropertiesIdentical(chartStyleJson1, chartStyleJson2)
  )
    return false;

  if (
    rangeOption.useNumberOfDays &&
    !areRangeDaysMonthsAndYearsPropertiesIdentical(chartStyleJson1, chartStyleJson2)
  )
    return false;

  return (
    !rangeOption.useStartDateAndEndDate ||
    areRangeStartAndEndDatePropertiesIdentical(chartStyleJson1, chartStyleJson2)
  );
}

function areColorSchemePropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  if (chartStyleJson1.colorScheme !== chartStyleJson2.colorScheme)
    return false;

  return (
    chartStyleJson1.colorScheme !== COLOR_SCHEME_CUSTOM.value ||
    areCustomColorSchemePropertiesIdentical(chartStyleJson1, chartStyleJson2)
  );
}

function areChartSizePropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  if (chartStyleJson1.chartSize !== chartStyleJson2.chartSize)
    return false;

  return (
    chartStyleJson1.chartSize !== CHART_SIZE_MENU_OPTION_CUSTOM.title ||
    areCustomWidthAndHeightPropertiesIdentical(chartStyleJson1, chartStyleJson2)
  );
}

function arePerfChartSymbolsIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  if (chartStyleJson1.perfChartSymbols.length !== chartStyleJson2.perfChartSymbols.length)
    return false;

  for (let i = 0; i < chartStyleJson1.perfChartSymbols.length; i++) {
    const perfChartSymbol1 = chartStyleJson1.perfChartSymbols[i];
    const perfChartSymbol2 = chartStyleJson2.perfChartSymbols[i];

    if (perfChartSymbol1.symbol !== perfChartSymbol2.symbol)
      return false;
  }

  return true;
}

function areIndicatorsIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  if (chartStyleJson1.sortedIndicators.length !== chartStyleJson2.sortedIndicators.length)
    return false;

  for (let i = 0; i < chartStyleJson1.sortedIndicators.length; i++) {
    const indicator1 = chartStyleJson1.sortedIndicators[i];
    const indicator2 = chartStyleJson2.sortedIndicators[i];

    if (
      indicator1.indicatorName !== indicator2.indicatorName ||
      indicator1.parameters !== indicator2.parameters ||
      indicator1.indicatorPosition !== indicator2.indicatorPosition ||
      indicator1.indicatorPriceStyle !== indicator2.indicatorPriceStyle ||
      indicator1.indicatorColor !== indicator2.indicatorColor ||
      indicator1.indicatorHeight !== indicator2.indicatorHeight ||
      indicator1.indicatorOpacity !== indicator2.indicatorOpacity ||
      indicator1.overlayName !== indicator2.overlayName ||
      indicator1.overlayParameters !== indicator2.overlayParameters
    )
      return false;
  }

  return true;
}

function areOverlaysIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  if (chartStyleJson1.sortedOverlays.length !== chartStyleJson2.sortedOverlays.length)
    return false;

  for (let i = 0; i < chartStyleJson1.sortedOverlays.length; i++) {
    const overlay1 = chartStyleJson1.sortedOverlays[i];
    const overlay2 = chartStyleJson2.sortedOverlays[i];

    if (
      overlay1.overlayName !== overlay2.overlayName ||
      overlay1.parameters !== overlay2.parameters ||
      overlay1.overlayStyle !== overlay2.overlayStyle ||
      overlay1.overlayColor !== overlay2.overlayColor ||
      overlay1.overlayOpacity !== overlay2.overlayOpacity
    )
      return false;
  }

  return true;
}

function areNonPerfChartPropertiesIdentical(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  return (
    chartStyleJson1.upColor === chartStyleJson2.upColor &&
    chartStyleJson1.downColor === chartStyleJson2.downColor &&
    chartStyleJson1.opacity === chartStyleJson2.opacity &&
    chartStyleJson1.isLogScale === chartStyleJson2.isLogScale &&
    chartStyleJson1.extendedHours === chartStyleJson2.extendedHours &&
    chartStyleJson1.fullQuote === chartStyleJson2.fullQuote &&
    chartStyleJson1.priceLabels === chartStyleJson2.priceLabels &&
    chartStyleJson1.solidCandles === chartStyleJson2.solidCandles &&
    chartStyleJson1.colorVolume === chartStyleJson2.colorVolume &&
    chartStyleJson1.chartSettingsVolumeMenuValue === chartStyleJson2.chartSettingsVolumeMenuValue
  );
}

export function areChartStyleObjectsEqual(chartStyleJson1: ChartStyle, chartStyleJson2: ChartStyle): boolean {
  if (!isChartStyle(chartStyleJson1) || !isChartStyle(chartStyleJson2))
    return false;

  if (chartStyleJson1 === chartStyleJson2)
    return true;

  if (
    !areChartTypesPropertiesIdentical(chartStyleJson1, chartStyleJson2) ||
    chartStyleJson1.period !== chartStyleJson2.period ||
    !areRangePropertiesIdentical(chartStyleJson1, chartStyleJson2) ||
    !areColorSchemePropertiesIdentical(chartStyleJson1, chartStyleJson2) ||
    !areChartSizePropertiesIdentical(chartStyleJson1, chartStyleJson2) ||
    chartStyleJson1.yAxisLabels !== chartStyleJson2.yAxisLabels ||
    chartStyleJson1.zoomThumbnail !== chartStyleJson2.zoomThumbnail ||
    chartStyleJson1.legends !== chartStyleJson2.legends ||
    chartStyleJson1.chartGrid !== chartStyleJson2.chartGrid
  )
    return false;

  if (chartStyleJson1.chartType === CHART_TYPE_OPTION_PERF_CHART.value)
    return true;

  return (
    areNonPerfChartPropertiesIdentical(chartStyleJson1, chartStyleJson2) &&
    areIndicatorsIdentical(chartStyleJson1, chartStyleJson2) &&
    areOverlaysIdentical(chartStyleJson1, chartStyleJson2)
  );
}
